/**
 * @module Model
 */

import { User } from '@feature/users/shared';
import { CompanyMemberRef, CompanyMemberRole } from '@feature/companies/shared';

/**
 * Class representing the application context, which holds information about the current user
 * and their membership in a company. This class provides various properties and methods to
 * determine the user's role, context, and permissions within the application.
 *
 * @class
 * @model
 */
export class AppContext {

	/**
	 * The currently authenticated user.
	 *
	 * @type {User}
	 */
	user: User;

	/**
	 * Reference to the user's company membership, if applicable.
	 *
	 * @type {CompanyMemberRef}
	 */
	companyMembership: CompanyMemberRef;

	/**
	 * Indicates whether the user can edit their profile.
	 *
	 * @type {boolean}
	 * @private
	 * @hidden
	 */
	private _canEditProfile: boolean;

	/**
	 * Indicates whether the user can create a new profile.
	 *
	 * @type {boolean}
	 * @private
	 * @hidden
	 */
	private _canCreateProfile: boolean;

	/**
	 * Checks if the current context points to an individual profile.
	 *
	 * @getter
	 * @returns {boolean} True if the context is personal (no company membership), otherwise false.
	 */
	get isPersonalContext(): boolean { return this.companyMembership == null; }

	/**
	 * Checks if the current context points to a company profile.
	 *
	 * @getter
	 * @returns {boolean} True if the context is a company, otherwise false.
	 */
	get isCompanyContext(): boolean { return ! this.isPersonalContext; }

	/**
	 * Checks if the user is an agent based on the current context.
	 *
	 * @getter
	 * @returns {boolean} True if the user is an agent, otherwise false.
	 */
	get isAgent(): boolean { return this.isPersonalContext ? this.user.isAgent : (this.isCompanyContext ? this.companyMembership.isAgent : false); }

	/**
	 * Checks if the user is a talent based on the current context.
	 *
	 * @getter
	 * @returns {boolean} True if the user is a talent, otherwise false.
	 */
	get isTalent(): boolean { return this.isPersonalContext && this.user.isTalent; }

	/**
	 * Checks if the user is a customer based on the current context.
	 *
	 * @getter
	 * @returns {boolean} True if the user is a customer, otherwise false.
	 */
	get isCustomer(): boolean { return this.isPersonalContext ? this.user.isCustomer : (this.isCompanyContext ? this.companyMembership.isCustomer : false); }

	/**
	 * Checks if the user has admin privileges.
	 *
	 * @getter
	 * @returns {boolean} True if the user is an admin, otherwise false.
	 */
	get isAdmin(): boolean { return this.user.isAdmin; }

	/**
	 * Checks if the user is a personal customer.
	 *
	 * @getter
	 * @returns {boolean} True if the user is a personal customer, otherwise false.
	 */
	get isPersonalCustomer(): boolean { return this.isCustomer && this.isPersonalContext; }

	/**
	 * Checks if the user is a company customer.
	 *
	 * @getter
	 * @returns {boolean} True if the user is a company customer, otherwise false.
	 */
	get isCompanyCustomer(): boolean { return this.isCustomer && this.isCompanyContext; }

	/**
	 * Checks if the user's company membership is classified as enterprise.
	 *
	 * @getter
	 * @returns {boolean} True if the company membership is enterprise, otherwise false.
	 */
	get isEnterprise(): boolean { return this.isCompanyContext && this.companyMembership.companyIsEnterprise; }

	/**
	 * Gets the name of the context (either 'Personal' or the company name).
	 *
	 * @getter
	 * @returns {string} The name of the context.
	 */
	get name(): string { return this.isPersonalContext ? 'Personal' : (this.isCompanyContext ? this.companyMembership.companyName : ''); }

	/**
	 * Gets the full name of the user or the company name based on the context.
	 *
	 * @getter
	 * @returns {string} The full name of the user or company.
	 */
	get fullName(): string { return this.isPersonalContext ? this.user?.fullName : (this.isCompanyContext ? this.companyMembership.companyName : ''); }

	/**
	 * Checks if the context is ready for use.
	 *
	 * @getter
	 * @returns {boolean} True if the context is ready, otherwise false.
	 */
	get isReady(): boolean { return this.redirectUrlStep === undefined; }

	/**
	 * Gets the role alias of the user based on their context.
	 *
	 * @getter
	 * @returns {string} The role alias ('agent', 'customer', 'talent', or an empty string).
	 */
	get roleAlias(): string { return this.isAgent ? 'agent' : (this.isCustomer ? 'customer' : (this.isTalent ? 'talent' : '')); }

	/**
	 * Checks if the user has an unknown type based on their migration status and context.
	 *
	 * @getter
	 * @returns {boolean} True if the user has an unknown type, otherwise false.
	 */
	get unknownType(): boolean { return ! this.user.isMigrated && ((this.isCompanyContext && this.companyMembership === undefined) || (this.isPersonalContext && ! this.user.isIndividual)); }

	/**
	 * Gets the redirect URL step based on the user's context and signup status.
	 *
	 * @getter
	 * @returns {string | undefined} The redirect URL step or undefined if the user is an admin.
	 */
	get redirectUrlStep(): string {
		if (this.isAdmin) return undefined;
		if (this.unknownType) return 'type';

		return this.isPersonalContext && this.user?.signupStep !== undefined
			? this.user?.signupStep
			: (this.isCompanyContext && this.companyMembership?.signupStep !== undefined
				? this.companyMembership.signupStep
				: undefined
			);
	}

	/**
	 * Constructs the redirect URL based on the user's context and signup step.
	 *
	 * @getter
	 * @returns {string} The constructed redirect URL.
	 */
	get redirectUrl(): string {
		if (this.unknownType) return '/profile/type';
		return `/profile/${this.redirectUrlStep || 'details'}`;
	}

	/**
	 * Determines whether the user can edit their profile based on the current context and signup status.
	 *
	 * This getter checks if the user is in a personal context and has either started or not completed
	 * the signup process, or if the user is a company member with the necessary roles to edit the company's
	 * profile. It sets the internal flag `_canEditProfile` accordingly and returns its value.
	 *
	 * @getter
	 * @returns {boolean} True if the user can edit the profile, otherwise false.
	 */
	get canEditProfile(): boolean {
		if (
			( // 1) Is a personal user profile and user started the sign-up process or finished it.
				this.isPersonalContext
                && (this.user?.signupStep !== 'type' || this.user?.signupStep === undefined)
			) || ( // 2) Is company member and has rights to edit the company's profile (e.g. owner.)
				this.isCompanyContext
                && this.hasMemberRoles([CompanyMemberRole.owner]) // Roles can expand if required.
			)
		) {
			this._canEditProfile = true;
		}

		return this._canEditProfile;
	}

	/**
	 * Determines whether the user can create a new profile based on the current context and signup status.
	 *
	 * This getter checks if the user is in a personal context with the appropriate signup step, or if the
	 * user is a company member with the necessary roles to create a profile. It also checks for contexts
	 * where no personal profile type has been selected. The internal flag `_canCreateProfile` is set
	 * accordingly and returned.
	 *
	 * @getter
	 * @returns {boolean} True if the user can create a profile, otherwise false.
	 */
	get canCreateProfile(): boolean {
		if (
			// 1) Is a personal user profile.
			(this.isPersonalContext && ! this.user.isCompany && this.user?.signupStep === 'type')
			// 2) Is company member.
			|| (this.isCompanyContext && ! this.user.isIndividual && this.hasMemberRoles([CompanyMemberRole.owner]))
			// 3) Is a context (e.g. member) without personal profile type selected.
			|| (this.user?.signupStep == null && this.user?.roles == null)
		) {
			this._canCreateProfile = true;
		} else {
			this._canCreateProfile = false;
		}

		return this._canCreateProfile;
	}

	/**
	 * Creates an instance of the AppContext.
	 *
	 * @constructor
	 * @param {User} [_user] - The user object representing the currently authenticated user.
	 * @param {CompanyMemberRef} [_companyMembership] - The company membership reference for the user, if applicable.
	 */
	constructor(
        private _user?: User,
        private _companyMembership?: CompanyMemberRef
	) {
		this.user = _user;
		this.companyMembership = _companyMembership;
		this._canCreateProfile = false;
		this._canEditProfile = false;
	}

	/**
	 * Checks if the current user's company membership includes any of the specified member roles.
	 *
	 * This method verifies whether the user's membership role exists and if it matches any of the
	 * roles provided in the input array. It returns true if there is a match; otherwise, it returns false.
	 *
	 * @param {Array<string>} memberRoles - An array of member roles to check against the user's membership role.
	 * @returns {boolean} True if the user's membership role matches one of the specified roles, otherwise false.
	 */
	public hasMemberRoles(memberRoles: Array<string>): boolean {
		return this.companyMembership?.memberRole && memberRoles.includes(this.companyMembership.memberRole.toString());
	}
}
